import React, { useState, useEffect, useRef } from "react";
import { api, sendMessage, uploadMedia, dataUrlToBase64, compressImage, mediaSrcById } from "../api";
import { Checkbox } from "./ui/checkbox";
import { WindowTypePicker } from "./WindowTypePicker";
import { galleryAvailable, saveToGallery, getSaveToGalleryPref, setSaveToGalleryPref } from "../lib/gallery";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Ruler, Camera, Image as ImageIcon, Loader2, Sparkles, Plus, Trash2, PenLine, Check, X } from "lucide-react";
import { VoiceInputButton } from "./VoiceInputButton";
import { annotateMeasurePhoto, annotateMeasurePhotoMulti, sizeEquals, cropUnitPreview } from "../lib/measureAnnotate";
import { MeasureBoxEditor } from "./MeasureBoxEditor";
import { ShapeDimsEditor } from "./ShapeDimsEditor";
import { SHAPE_BY_ID, shapeWH, shapeExtraText, shapeComplete, isDoorShape } from "../lib/windowShapes";

/** Industry-standard unit styles (kept in English — matches the job PDFs). */
export const UNIT_SUBTYPES = {
  window: [
    ["double_hung", "Double Hung"],
    ["single_hung", "Single Hung"],
    ["glider", "Glider"],
    ["glider_triple", "Glider Triple"],
    ["casement", "Casement"],
    ["casement_double", "Casement Double"],
    ["casement_triple", "Casement Triple"],
    ["awning", "Awning"],
    ["picture", "Picture Window"],
    ["fixed", "Fixed Window"],
    ["specialty", "Specialty Window"],
  ],
  // sidelights / transom are door CONFIGURATIONS (DOOR_SHAPES), not types
  door: [
    ["entry", "Entry Door"],
    ["french", "French Door"],
    ["sliding", "Sliding Glass Door"],
    ["patio", "Patio Door"],
    ["storm", "Storm Door"],
  ],
};
const SUBTYPE_LABEL = Object.fromEntries([...UNIT_SUBTYPES.window, ...UNIT_SUBTYPES.door]);
const DOOR_IDS = new Set(UNIT_SUBTYPES.door.map(([v]) => v));

/**
 * Visual measurement flow — built to REPORT WRONG-SIZED windows/doors.
 *
 * photo (camera or device) → AI finds ALL the units in the opening (mulled
 * windows, door + transom, sidelites — real photos AND hand-drawn sketches)
 * → the dashed boxes are MANUALLY ADJUSTABLE (move/resize/add/delete)
 * → "is it from this job?" (switchable to ANOTHER job) → for EACH unit: pick
 * the PDF entry + type the REAL measured size → optional OVERALL opening size
 * → ONE annotated photo with every unit → session report loops photo by photo
 * → a single message goes out (measurement or a CATALOG with all photos).
 */
export function MeasurementDialog({ open, onClose, conversation, extraMeta, editing = null, onEdited }) {
  const { user, t } = useApp();
  // Edit mode (sender, ≤ 2h): original photo + boxes + typed values come back from meta.src
  const [prefill, setPrefill] = useState(null); // [{...unit entry}]
  const [saveGallery, setSaveGallery] = useState(getSaveToGalleryPref); // RAW photo (before any marking) → phone gallery (native)
  const [extraPhotos, setExtraPhotos] = useState([]); // optional extra shots of THIS opening: { dataUrl } (new) | { mediaId } (kept from edit)
  const extraRef = useRef(null);
  const rawSavedRef = useRef(null); // dataUrl already saved, so a photo is never saved twice
  const fromCameraRef = useRef(false);
  const [srcPhoto, setSrcPhoto] = useState(null); // original (un-annotated) dataURL as sent
  const [srcMediaId, setSrcMediaId] = useState(null);
  const [step, setStep] = useState("photo"); // photo | adjust | job | pickjob | location | value | overall | preview | more
  const [photo, setPhoto] = useState(null); // dataURL
  const [analyzing, setAnalyzing] = useState(false);
  const [units, setUnits] = useState([]); // [{box:{x,y,w,h}, kind}]
  const [selUnit, setSelUnit] = useState(-1);
  const [config, setConfig] = useState("single");
  const [sketch, setSketch] = useState(false);
  const [detFound, setDetFound] = useState(null); // null until analyzed
  const [job, setJob] = useState(null); // ACTIVE job (conversation's, today's, or picked)
  const [jobs, setJobs] = useState(null); // list for the "another job" picker
  const [jobSearch, setJobSearch] = useState("");
  const [openings, setOpenings] = useState(null); // null = not loaded
  const [opLoading, setOpLoading] = useState(false);
  const [uIdx, setUIdx] = useState(0); // unit currently being measured
  const [unitData, setUnitData] = useState([]); // measured per unit
  const [unitCrops, setUnitCrops] = useState([]); // zoomed preview per unit (which window is this?)
  const [opening, setOpening] = useState(null); // selected PDF opening (current unit)
  const [location, setLocation] = useState("");
  const [shape, setShape] = useState("rectangle"); // specialty window shape (half circle, springline, …)
  const [dims, setDims] = useState({}); // all measurements the shape needs (w, h, leg, top, legL, legR, d, side)
  const wh = shapeWH(shape, dims);
  const realW = wh.w, realH = wh.h;
  const [note, setNote] = useState("");
  const [overallW, setOverallW] = useState("");
  const [overallH, setOverallH] = useState("");
  const [overallUsed, setOverallUsed] = useState(null); // {w,h} actually stamped
  const [annotated, setAnnotated] = useState(null);
  const [items, setItems] = useState([]); // session report (catalog when > 1)
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const jobId = conversation?.job_id || extraMeta?.linked_job_id || editing?.meta?.linked_job_id || null;
  const effJobId = job?.id || jobId || null;
  const jobLabel = job ? `#${job.job_number}${job.customer_name ? ` — ${job.customer_name}` : ""}` : "";

  const resetUnitInputs = () => {
    setOpening(null); setLocation(""); setDims({}); setShape("rectangle"); setNote("");
  };
  const resetShot = () => {
    setPhoto(null); setUnits([]); setSelUnit(-1); setConfig("single"); setSketch(false);
    setDetFound(null); setUIdx(0); setUnitData([]); setUnitCrops([]); setOverallW(""); setOverallH("");
    setOverallUsed(null); setAnnotated(null); setExtraPhotos([]); resetUnitInputs(); setStep("photo");
  };
  const onPickExtra = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - extraPhotos.length);
    e.target.value = "";
    for (const f of files) {
      try { const { dataUrl } = await compressImage(f); setExtraPhotos((prev) => [...prev, { dataUrl }]); } catch (err) { /* skip bad file */ }
    }
  };
  const resetAll = () => { resetShot(); setOpenings(null); setItems([]); setJobSearch(""); setPrefill(null); setSrcPhoto(null); setSrcMediaId(null); };

  // Edit mode: rebuild the session from meta.src (original photo, boxes, typed values).
  // Catalog messages: `editing.catalogIndex` points at the opening being edited.
  const editMeta = editing?.catalogIndex != null ? editing?.meta?.items?.[editing.catalogIndex] : editing?.meta;
  useEffect(() => {
    if (!open || !editMeta?.src?.media_id) return;
    const src = editMeta.src;
    let cancelled = false;
    (async () => {
      try {
        const blob = await (await fetch(mediaSrcById(src.media_id))).blob();
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob);
        });
        if (cancelled) return;
        setSrcPhoto(dataUrl); setSrcMediaId(src.media_id); setPhoto(dataUrl);
        setUnits((src.units || []).map((u) => ({ box: u.box, kind: u.kind || "window", subtype: u.subtype || "" })));
        setSelUnit(src.units?.length ? 0 : -1);
        setConfig(src.units?.length > 1 ? "multi_window" : "single");
        setSketch(!!src.sketch); setDetFound(true);
        setPrefill(src.unit_data || []);
        if (src.overall) { setOverallW(src.overall.w || ""); setOverallH(src.overall.h || ""); }
        setExtraPhotos((editMeta.extra_media_ids || []).map((mediaId) => ({ mediaId })));
        setStep("adjust");
      } catch (e) {
        toast.error(t("something_wrong"));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  useEffect(() => {
    if (open && jobId) {
      api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setJob(null));
    } else if (open && !jobId) {
      // DM / conversation without a job: offer TODAY's scheduled job of this user
      api.get("/jobs", { params: { user_id: user.id } }).then((r) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const found = (r.data || []).find((j) => {
          if (!j.scheduled_date) return false;
          const start = new Date(j.scheduled_date); start.setHours(0, 0, 0, 0);
          const days = Math.max(1, j.duration_days || 1);
          const end = new Date(start); end.setDate(end.getDate() + days - 1);
          return today >= start && today <= end;
        });
        setJob(found || null);
      }).catch(() => setJob(null));
    }
    if (!open) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  // ---------- photo intake: native camera (saves to device gallery) or device picker ----------

  const handlePhoto = async (dataUrl) => {
    setPhoto(dataUrl);
    setAnalyzing(true);
    setDetFound(null);
    setUnits([]); setSelUnit(-1); setConfig("single"); setSketch(false);
    setStep("adjust");
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("image", blob, "measure.jpg");
      const r = await api.post("/measure/analyze-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const us = (r.data?.units || [])
        .filter((u) => u && u.box)
        .map((u) => {
          const subtype = u.subtype && SUBTYPE_LABEL[u.subtype] ? u.subtype : "";
          return { box: u.box, kind: u.kind === "door" || DOOR_IDS.has(subtype) ? "door" : "window", subtype };
        });
      setUnits(us);
      setConfig(r.data?.config || (us.length > 1 ? "multi_window" : "single"));
      setSketch(!!r.data?.sketch);
      setDetFound(us.length > 0);
      setSelUnit(us.length ? 0 : -1);
    } catch (err) {
      setDetFound(false);
    } finally {
      setAnalyzing(false);
    }
  };

  /** Native Capacitor camera/photos. Returns false when unavailable (→ web input fallback). */
  const nativeShot = async (fromCamera) => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("Camera")) return false;
      const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
      try {
        const shot = await Camera.getPhoto({
          quality: 85,
          width: 1600,
          resultType: CameraResultType.DataUrl,
          source: fromCamera ? CameraSource.Camera : CameraSource.Photos,
          saveToGallery: false, // the checkbox on the next screen decides (raw photo, before marking)
          correctOrientation: true,
        });
        fromCameraRef.current = fromCamera;
        if (shot?.dataUrl) await handlePhoto(shot.dataUrl);
        return true;
      } catch (err) {
        const msg = String(err?.message || err || "").toLowerCase();
        if (/cancel/.test(msg)) return true; // user closed the picker
        // permission denied / plugin failure → fall back to the system file picker (needs no permission)
        toast.info(t("measure_picker_fallback"));
        return false;
      }
    } catch (err) {
      return false;
    }
  };

  const openCamera = async () => { if (!(await nativeShot(true))) cameraRef.current?.click(); };
  const openGallery = () => galleryRef.current?.click();

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    fromCameraRef.current = e.target === cameraRef.current;
    e.target.value = "";
    if (!file) return;
    try {
      const { dataUrl } = await compressImage(file);
      await handlePhoto(dataUrl);
    } catch (err) {
      toast.error(t("something_wrong"));
    }
  };

  // ---------- adjust step: confirm configuration + edit boxes ----------

  const configQuestion = () => {
    if (analyzing) return t("measure_analyzing");
    if (!units.length) return t("measure_draw_hint");
    if (units.length === 1) return t("measure_adjust_single");
    switch (config) {
      case "door_transom": return t("measure_door_transom_q");
      case "door_sidelites": return t("measure_door_sidelites_q");
      case "door_combo": return t("measure_door_combo_q");
      default: return t("measure_multi_window_q");
    }
  };

  const addBox = () => {
    // cascade new boxes so they don't stack on top of each other
    const off = Math.min(0.3, units.length * 0.07);
    const next = [...units, { box: { x: 0.18 + off, y: 0.18 + off, w: 0.38, h: 0.38, angle: 0 }, kind: "window" }];
    setUnits(next);
    setSelUnit(next.length - 1);
  };

  const removeBox = () => {
    if (selUnit < 0) return;
    const next = units.filter((_, i) => i !== selUnit);
    setUnits(next);
    setSelUnit(next.length ? Math.max(0, selUnit - 1) : -1);
  };

  const setUnitProp = (idx, patch) => {
    setUnits((prev) => prev.map((u, i) => (i === idx ? { ...u, ...patch } : u)));
  };

  const confirmUnits = () => {
    if (!units.length) { toast.info(t("measure_need_box")); return; }
    saveRawToPhone();
    setUnitData([]); setUIdx(0); resetUnitInputs();
    // zoomed crop per unit so the user always knows WHICH window is being measured
    setUnitCrops([]);
    Promise.all(units.map((u) => cropUnitPreview({ dataUrl: photo, box: u.box }).catch(() => null)))
      .then(setUnitCrops)
      .catch(() => setUnitCrops([]));
    if (editing) { // edit mode: the job is already known — jump straight to the values
      if (openings !== null) startMeasuring(openings);
      else if (effJobId) loadOpeningsFor(effJobId);
      else startMeasuring([]);
      return;
    }
    if (job || effJobId) setStep("job");
    else { setStep("pickjob"); loadJobsList(); }
  };

  /** Edit mode: restore what was typed for unit `idx` (location, PDF opening, shape, dims, note). */
  const applyPrefill = (idx, list) => {
    const p = prefill?.[idx];
    if (!p) return false;
    const o = (list || []).find((x) => (p.label && x.label === p.label) || (!p.label && p.expected && x.size === p.expected)) || null;
    setOpening(o);
    setLocation(p.location || "");
    setShape(p.shape || "rectangle");
    setDims(p.dims && Object.keys(p.dims).length ? p.dims : { w: p.realW || "", h: p.realH || "" });
    setNote(p.note || "");
    setStep("value");
    return true;
  };

  // ---------- job selection ----------

  const loadJobsList = () => {
    if (jobs === null) {
      api.get("/jobs", { params: { user_id: user.id } })
        .then((r) => setJobs((r.data || []).sort((a, b) => (b.scheduled_date || "").localeCompare(a.scheduled_date || ""))))
        .catch(() => setJobs([]));
    }
  };

  const startMeasuring = (list) => {
    setUIdx(0); resetUnitInputs();
    if (applyPrefill(0, list)) return;
    setStep(list && list.length ? "location" : "value");
    if (!(list && list.length)) toast.info(t("measure_no_pdf"));
  };

  const loadOpeningsFor = async (jid) => {
    setOpLoading(true);
    try {
      const r = await api.get("/measure/job-openings", { params: { job_id: jid } });
      const list = r.data?.openings || [];
      setOpenings(list);
      startMeasuring(list);
    } catch (e) {
      setOpenings([]);
      setUIdx(0); resetUnitInputs();
      setStep("value");
    } finally {
      setOpLoading(false);
    }
  };

  const answerJob = async (yes) => {
    if (!yes) {
      setStep("pickjob");
      loadJobsList();
      return;
    }
    if (openings !== null) { // already loaded for a previous photo of this session
      setUIdx(0); resetUnitInputs();
      setStep(openings.length ? "location" : "value");
      return;
    }
    await loadOpeningsFor(effJobId);
  };

  const pickJob = async (j) => {
    setJob(j);
    setOpenings(null);
    await loadOpeningsFor(j.id);
  };

  const skipJob = () => {
    setJob(null); // user explicitly opted out of linking a job
    setUIdx(0); resetUnitInputs();
    setStep("value");
  };

  const pickOpening = (o) => {
    setOpening(o || null);
    // unit number (105, 101...) always travels WITH the location name
    setLocation(o ? [o.label, o.location].filter(Boolean).join(" · ") : "");
    if (o) {
      // PDF pre-fills the unit TYPE, the SHAPE and its extra dims — all editable
      const kind = o.kind === "door" ? "door" : "window";
      const okSub = (UNIT_SUBTYPES[kind] || []).some(([v]) => v === o.subtype);
      if (okSub) setUnitProp(uIdx, { kind, subtype: o.subtype });
      const sId = SHAPE_BY_ID[o.shape] ? o.shape : "rectangle";
      setShape(sId);
      setDims(o.shape_dims?.side ? { side: o.shape_dims.side } : {}); // real sizes are MEASURED, never copied from the PDF
    }
    setStep("value");
  };

  const filteredJobs = (jobs || []).filter((j) => {
    const q = jobSearch.trim().toLowerCase();
    if (!q) return true;
    return `${j.job_number} ${j.customer_name || ""} ${j.city || ""}`.toLowerCase().includes(q);
  }).slice(0, 30);

  // ---------- per-unit measuring ----------

  const matchW = opening ? sizeEquals(realW, opening.width) : null;
  const matchH = opening ? sizeEquals(realH, opening.height) : null;
  // PDF says one geometry (e.g. half circle) and the installer picked another → wrong product, flag it
  const pdfShape = opening ? (opening.shape || "rectangle") : null;
  const shapeDiverges = !!opening && pdfShape !== shape;
  const match = opening ? (matchW && matchH && !shapeDiverges) : null;

  const dimLabel = (k) => t(k === "side_left" || k === "side_right" ? k : `measure_dim_${k}`);
  const extraText = shapeExtraText(shape, dims, dimLabel);
  const unitReady = shapeComplete(shape, dims) && !!realW.trim() && !!realH.trim();

  const saveUnit = async () => {
    if (!unitReady) return;
    const shapeName = shape !== "rectangle" ? t(`shape_${shape}`) : "";
    const entry = {
      location: location.trim(),
      label: opening?.label || "",
      expected: opening?.size || "",
      value: [shapeName, shape === "circle" ? `Ø ${realW.trim()}` : `${realW.trim()} x ${realH.trim()}`, extraText].filter(Boolean).join(" · "),
      realW: realW.trim(),
      realH: realH.trim(),
      shape,
      shapeExpected: shapeDiverges ? pdfShape : "",
      dims: Object.fromEntries(Object.entries(dims).filter(([k, v]) => !k.startsWith("_") && v)),
      extraText,
      match,
      kind: units[uIdx]?.kind || "window",
      subtype: units[uIdx]?.subtype ? (SUBTYPE_LABEL[units[uIdx].subtype] || "") : "",
      note: note.trim(),
    };
    const next = [...unitData];
    next[uIdx] = entry;
    setUnitData(next);
    if (uIdx + 1 < units.length) {
      setUIdx(uIdx + 1);
      resetUnitInputs();
      if (!applyPrefill(uIdx + 1, openings)) setStep(openings && openings.length ? "location" : "value");
    } else if (units.length > 1) {
      setStep("overall");
    } else {
      await buildPreview(next, null);
    }
  };

  const finishOverall = async (withOverall) => {
    const ov = withOverall && overallW.trim() && overallH.trim()
      ? { w: overallW.trim(), h: overallH.trim() }
      : null;
    setOverallUsed(ov);
    await buildPreview(unitData, ov);
  };

  // ---------- annotated preview ----------

  const buildPreview = async (data, ov) => {
    try {
      let url;
      if (units.length === 1 && !ov) {
        const d = data[0];
        url = await annotateMeasurePhoto({
          dataUrl: photo,
          box: units[0].box,
          widthText: d.realW,
          heightText: d.realH,
          shape: d.shape, side: d.dims?.side, extraText: d.extraText, dims: d.dims,
          locationText: [d.location, d.shape && d.shape !== "rectangle" ? t(`shape_${d.shape}`) : "", d.subtype, job ? `#${job.job_number}` : ""].filter(Boolean).join(" · "),
          expectedText: [d.expected ? `PDF: ${d.expected}` : "", d.shapeExpected ? `${t("measure_pdf_shape")}: ${t(`shape_${d.shapeExpected}`)} ≠ ${t(`shape_${d.shape}`)}` : ""].filter(Boolean).join(" · "),
          noteText: d.note || "",
          match: d.match,
          oldLabel: t("old_measurements_wrong"),
        });
      } else {
        url = await annotateMeasurePhotoMulti({
          dataUrl: photo,
          units: units.map((u, i) => ({
            box: u.box,
            widthText: data[i]?.realW || "",
            heightText: data[i]?.realH || "",
            shape: data[i]?.shape, side: data[i]?.dims?.side, extraText: data[i]?.extraText || "", dims: data[i]?.dims,
            labelText: data[i]?.label ? `${i + 1} · ${data[i].label}` : String(i + 1),
            subtypeText: data[i]?.subtype || "",
            expectedText: [data[i]?.expected || "", data[i]?.shapeExpected ? `${t("measure_pdf_shape")}: ${t(`shape_${data[i].shapeExpected}`)}` : ""].filter(Boolean).join(" · "),
            noteText: data[i]?.note || "",
            match: data[i]?.match ?? null,
          })),
          locationText: [job ? `#${job.job_number}` : "", sketch ? t("measure_sketch_flag") : ""].filter(Boolean).join(" · "),
          overall: ov ? { widthText: ov.w, heightText: ov.h } : null,
          oldLabel: t("old_measurements_wrong"),
        });
      }
      setAnnotated(url);
      setStep("preview");
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  // ---------- session report: add item, then send one message (catalog when > 1) ----------

  const buildItem = () => {
    const wrong = unitData.filter((u) => u.match === false).length;
    const allMatch = wrong > 0 ? false : (unitData.every((u) => u.match === true) ? true : null);
    const single = unitData.length === 1;
    return {
      photo: annotated,
      location: single
        ? unitData[0].location
        : (unitData.map((u) => u.label).filter(Boolean).join(", ") || `${unitData.length} ${t("measure_units_short")}`),
      value: single ? unitData[0].value : `${unitData.length} · ${wrong} ${t("measure_wrong_count")}`,
      expected: single ? unitData[0].expected : "",
      match: allMatch,
      kind: units[0]?.kind || "window",
      subtype: single ? (unitData[0].subtype || "") : "",
      note: single ? unitData[0].note : "",
      shape: single ? (unitData[0].shape || "rectangle") : "",
      shapeExpected: single ? (unitData[0].shapeExpected || "") : "",
      dims: single ? (unitData[0].dims || {}) : undefined,
      units: single ? undefined : unitData.map(({ realW: _w, realH: _h, extraText: _e, ...rest }) => rest),
      overall: overallUsed ? `${overallUsed.w} x ${overallUsed.h}` : "",
      extraPhotos,
      // everything needed to re-open this photo for editing (≤ 2h)
      src: { photo, units: units.map((u) => ({ box: u.box, kind: u.kind, subtype: u.subtype || "" })), unit_data: unitData, sketch, overall: overallUsed },
    };
  };

  /** Checkbox after the shot: the RAW photo (no boxes, no sizes drawn) goes to the phone gallery. */
  const saveRawToPhone = async () => {
    if (!saveGallery || !galleryAvailable() || !photo || !fromCameraRef.current || rawSavedRef.current === photo) return;
    rawSavedRef.current = photo;
    try {
      const n = await saveToGallery([{ path: photo, kind: "photo", fileName: `measure-${job?.job_number || "onsite"}-${Date.now()}.jpg` }], job);
      if (n) toast.success(t("saved_to_gallery_n").replace("{n}", String(n)));
    } catch (e) { /* gallery failure must not block the measurement */ }
  };

  const addToReport = () => {
    if (!annotated || !unitData.length) return;
    setItems((prev) => [...prev, buildItem()]);
    resetShot();
    setStep("more");
  };

  const uploadAnnotated = (dataUrl, n) => uploadMedia({
    base64: dataUrlToBase64(dataUrl), contentType: "image/jpeg", kind: "image", jobId: effJobId,
    uploadedBy: user.id, filename: `measure-${Date.now()}-${n}.jpg`,
  });

  /** Extra shots of an opening → media ids (kept ones pass through). */
  const uploadExtras = async (list) => {
    const ids = [];
    for (const x of list || []) {
      if (x.mediaId) { ids.push(x.mediaId); continue; }
      const media = await uploadMedia({
        base64: dataUrlToBase64(x.dataUrl), contentType: "image/jpeg", kind: "image", jobId: effJobId,
        category: "measure_extra", uploadedBy: user.id, filename: `measure-extra-${Date.now()}-${ids.length + 1}.jpg`,
      });
      ids.push(media.id);
    }
    return ids;
  };

  /** Original (un-annotated) photo kept aside — not linked to the job so it never shows up in galleries. */
  const uploadSrc = async (src) => {
    const { photo: _p, ...rest } = src;
    if (editing && srcMediaId && src.photo === srcPhoto) return { media_id: srcMediaId, ...rest };
    const media = await uploadMedia({
      base64: dataUrlToBase64(src.photo), contentType: "image/jpeg", kind: "image", jobId: null,
      category: "measure_src", uploadedBy: user.id, filename: `measure-src-${Date.now()}.jpg`,
    });
    return { media_id: media.id, ...rest };
  };

  const singleMeta = (it, srcMeta, extraIds) => ({
    ...(extraIds && extraIds.length ? { extra_media_ids: extraIds } : {}),
    location: it.location, label: it.label || "", value: it.value, expected: it.expected,
    match: it.match, kind: it.kind, subtype: it.subtype || "", note: it.note,
    ...(it.shape && it.shape !== "rectangle" ? { shape: it.shape, dims: it.dims } : {}),
    ...(it.shapeExpected ? { shape_expected: it.shapeExpected } : {}),
    ...(it.units ? { units: it.units } : {}),
    ...(it.overall ? { overall: it.overall } : {}),
    ...(srcMeta ? { src: srcMeta } : {}),
  });

  const saveEdit = async () => {
    if (!annotated || !unitData.length || saving) return;
    setSaving(true);
    try {
      const it = buildItem();
      const media = await uploadAnnotated(it.photo, (editing.catalogIndex ?? 0) + 1);
      const srcMeta = await uploadSrc(it.src);
      const extraIds = await uploadExtras(it.extraPhotos);
      let body;
      if (editing.catalogIndex != null) {
        const idx = editing.catalogIndex;
        const items = (editing.meta.items || []).map((x, i) => (i === idx ? singleMeta(it, srcMeta, extraIds) : x));
        const allIds = editing.media_ids?.length ? editing.media_ids : (editing.media || []).map((m) => m.id);
        const oldExtras = new Set((editing.meta.items || []).flatMap((x) => x.extra_media_ids || []));
        const mains = allIds.filter((id) => !oldExtras.has(id)); // annotated photos, one per opening, in order
        mains[idx] = media.id;
        const mediaIds = [...mains, ...items.flatMap((x) => x.extra_media_ids || [])];
        const wrong = items.filter((x) => x.match === false).length;
        body = {
          user_id: user.id, content: editing.content, media_ids: mediaIds,
          meta: { ...editing.meta, items, value: `${items.length} · ${wrong} ${t("measure_wrong_count")}` },
        };
      } else {
        body = { user_id: user.id, content: it.location || t("measurement"), media_ids: [media.id, ...extraIds], meta: singleMeta(it, srcMeta, extraIds) };
      }
      const res = await api.patch(`/messages/${editing.id}/measurement`, body);
      toast.success(t("saved"));
      onEdited && onEdited(res.data);
      resetAll();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "measure_edit_expired" ? t("measure_edit_expired") : t("something_wrong"));
    } finally {
      setSaving(false);
    }
  };

  const sendAll = async () => {
    if (!items.length || saving) return;
    setSaving(true);
    try {
      const mediaIds = [];
      for (const it of items) {
        const media = await uploadAnnotated(it.photo, mediaIds.length + 1);
        mediaIds.push(media.id);
      }
      const linkMetaExtra = job && job.id !== conversation?.job_id ? { linked_job_id: job.id } : {};
      if (items.length === 1) {
        const it = items[0];
        const srcMeta = await uploadSrc(it.src).catch(() => null);
        const extraIds = await uploadExtras(it.extraPhotos);
        await sendMessage({
          conversation_id: conversation.id,
          sender_id: user.id,
          type: "measurement",
          content: it.location || t("measurement"),
          media_ids: [...mediaIds, ...extraIds],
          meta: { ...singleMeta(it, srcMeta, extraIds), ...linkMetaExtra, ...(extraMeta || {}) },
        });
      } else {
        const wrong = items.filter((it) => it.match === false).length;
        const catalogItems = [];
        const extraAll = [];
        for (const it of items) {
          const srcMeta = await uploadSrc(it.src).catch(() => null);
          const extraIds = await uploadExtras(it.extraPhotos);
          extraAll.push(...extraIds);
          catalogItems.push(singleMeta(it, srcMeta, extraIds));
        }
        await sendMessage({
          conversation_id: conversation.id,
          sender_id: user.id,
          type: "measurement",
          content: `${t("measure_catalog")} (${items.length})`,
          media_ids: [...mediaIds, ...extraAll],
          meta: {
            catalog: true,
            items: catalogItems,
            value: `${items.length} · ${wrong} ${t("measure_wrong_count")}`,
            location: job ? `#${job.job_number}` : "",
            ...linkMetaExtra, ...(extraMeta || {}),
          },
        });
      }
      toast.success(t("measure_sent"));
      resetAll();
      onClose();
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setSaving(false);
    }
  };

  const unitProgress = units.length > 1 ? `${t("measure_piece")} ${uIdx + 1} ${t("measure_of")} ${units.length}` : "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) return; if (items.length && !editing && !window.confirm(t("measure_close_confirm").replace("{n}", String(items.length)))) return; onClose(); }}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="measurement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Ruler className="h-5 w-5 text-primary" /> {editing ? t("measure_edit_title") : t("measurement")}
            {items.length > 0 && (
              <Badge variant="secondary" className="ml-1" data-testid="measure-report-count">{items.length}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* -------- STEP 1: photo (camera saves to gallery / device picker) -------- */}
        {step === "photo" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("measure_photo_hint")}</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="measure-take-photo-btn"
                onClick={openCamera}
                className="h-36 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 flex flex-col items-center justify-center gap-2 text-primary transition-colors"
              >
                <Camera className="h-8 w-8" />
                <span className="font-semibold text-xs text-center px-2">{t("measure_take_photo_cam")}</span>
              </button>
              <button
                data-testid="measure-gallery-btn"
                onClick={openGallery}
                className="h-36 rounded-2xl border-2 border-dashed border-border bg-muted/40 hover:bg-muted flex flex-col items-center justify-center gap-2 text-muted-foreground transition-colors"
              >
                <ImageIcon className="h-8 w-8" />
                <span className="font-semibold text-xs text-center px-2">{t("measure_from_gallery")}</span>
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <PenLine className="h-3.5 w-3.5 shrink-0" /> {t("measure_sketch_hint")}
            </p>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickFile} data-testid="measure-file-input" />
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="measure-gallery-input" />
            {items.length > 0 && !editing && (
              <div className="rounded-xl border bg-muted/30 p-3 space-y-2" data-testid="measure-pending-report">
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {items.map((it, i) => (
                    <img key={i} src={it.photo} alt="" className="h-12 w-12 rounded-lg object-cover border shrink-0" />
                  ))}
                </div>
                <Button data-testid="measure-finish-send" className="w-full" onClick={sendAll} disabled={saving}>
                  <Check className="h-4 w-4 mr-1" /> {saving ? t("sending") : `${t("measure_finish_send")} (${items.length})`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* -------- STEP 2: confirm configuration + ADJUST the dashed boxes -------- */}
        {step === "adjust" && photo && (
          <div className="space-y-3">
            {galleryAvailable() && fromCameraRef.current && !editing && (
              <label className="flex items-center gap-2.5 px-1 cursor-pointer select-none" data-testid="measure-save-gallery-row">
                <Checkbox data-testid="measure-save-gallery-toggle" checked={saveGallery}
                          onCheckedChange={(v) => { setSaveGallery(!!v); setSaveToGalleryPref(!!v); }} />
                <span className="text-sm">{t("measure_save_phone")}</span>
              </label>
            )}
            <div className="text-sm font-medium flex items-start gap-1.5" data-testid="measure-config-question">
              {analyzing
                ? <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-0.5" />
                : detFound ? <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" /> : null}
              <span>
                {configQuestion()}
                {!analyzing && units.length > 1 && (
                  <span className="text-primary font-bold"> ({units.length} {t("measure_units_found")})</span>
                )}
                {!analyzing && sketch && (
                  <Badge variant="secondary" className="ml-1.5 text-[9px] align-middle" data-testid="measure-sketch-badge">{t("measure_sketch_flag")}</Badge>
                )}
              </span>
            </div>
            <MeasureBoxEditor
              photo={photo}
              boxes={units}
              selected={selUnit}
              onSelect={setSelUnit}
              onChange={setUnits}
              onAdd={addBox}
            />
            <p className="text-[11px] text-muted-foreground">{t("measure_adjust_hint")}</p>
            {selUnit >= 0 && units[selUnit] && (
              <div className="rounded-xl border bg-muted/30 px-3 py-2.5" data-testid="measure-box-props">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold shrink-0">{t("measure_piece")} {selUnit + 1}:</span>
                  <div className="flex gap-1 flex-1">
                    <Button
                      size="sm"
                      variant={units[selUnit].kind === "window" ? "default" : "outline"}
                      className="h-8 flex-1 text-xs"
                      onClick={() => setUnitProp(selUnit, { kind: "window", subtype: "" })}
                      data-testid="measure-kind-window"
                    >
                      {t("measure_window")}
                    </Button>
                    <Button
                      size="sm"
                      variant={units[selUnit].kind === "door" ? "default" : "outline"}
                      className="h-8 flex-1 text-xs"
                      onClick={() => setUnitProp(selUnit, { kind: "door", subtype: "" })}
                      data-testid="measure-kind-door"
                    >
                      {t("measure_door")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button data-testid="measure-add-box" variant="outline" size="sm" className="flex-1" onClick={addBox}>
                <Plus className="h-4 w-4 mr-1" /> {t("measure_add_box")}
              </Button>
              <Button data-testid="measure-remove-box" variant="outline" size="sm" className="flex-1" onClick={removeBox} disabled={selUnit < 0 || !units.length}>
                <Trash2 className="h-4 w-4 mr-1" /> {t("measure_remove_box")}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button data-testid="measure-retake-btn" variant="ghost" size="sm" onClick={resetShot}>
                <Camera className="h-4 w-4 mr-1" /> {t("measure_retake")}
              </Button>
              <Button data-testid="measure-boxes-continue" className="flex-1" onClick={confirmUnits} disabled={analyzing || !units.length}>
                {t("measure_continue")}
              </Button>
            </div>
          </div>
        )}

        {/* photo thumbnail (later steps) */}
        {["job", "location", "value", "overall"].includes(step) && photo && (
          <div className="flex items-center gap-3">
            <img src={photo} alt="" className="h-14 w-14 rounded-lg object-cover border" data-testid="measure-thumb" />
            <div className="text-xs text-muted-foreground flex-1">
              {units.length > 1
                ? <span className="font-medium text-foreground">{units.length} {t("measure_units_found")}</span>
                : (units[0]?.kind === "door" ? t("measure_door") : t("measure_window"))}
              {sketch ? ` · ${t("measure_sketch_flag")}` : ""}
            </div>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={resetShot} data-testid="measure-retake-btn">
              <Camera className="h-3.5 w-3.5 mr-1" /> {t("measure_retake")}
            </Button>
          </div>
        )}

        {/* -------- STEP 3: is it from this job? -------- */}
        {step === "job" && (
          <div className="space-y-3">
            <p className="text-sm font-medium" data-testid="measure-job-question">
              {(units[0]?.kind === "door" ? t("measure_job_q_door") : t("measure_job_q_window"))} <span className="text-primary font-bold">{jobLabel}</span>?
            </p>
            {opLoading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2 py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("measure_loading_pdf")}
              </div>
            ) : (
              <div className="flex gap-2">
                <Button data-testid="measure-job-yes" className="flex-1" onClick={() => answerJob(true)}>{t("yes")}</Button>
                <Button data-testid="measure-job-no" variant="outline" className="flex-1" onClick={() => answerJob(false)}>
                  {t("measure_job_no_other")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* -------- STEP 3b: pick ANOTHER job -------- */}
        {step === "pickjob" && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("measure_pick_job")}</p>
            <Input
              data-testid="measure-job-search"
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              placeholder={t("measure_search_job")}
            />
            <div className="max-h-[34vh] overflow-y-auto space-y-1.5" data-testid="measure-jobs-list">
              {jobs === null && (
                <div className="text-sm text-muted-foreground py-3 text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> ...
                </div>
              )}
              {opLoading && (
                <div className="text-sm text-muted-foreground py-3 text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("measure_loading_pdf")}
                </div>
              )}
              {!opLoading && filteredJobs.map((j) => (
                <button
                  key={j.id}
                  data-testid="measure-job-item"
                  onClick={() => pickJob(j)}
                  className="w-full text-left rounded-xl border px-3 py-2 hover:bg-muted/60 transition-colors"
                >
                  <div className="text-sm font-semibold">#{j.job_number}{j.customer_name ? ` — ${j.customer_name}` : ""}</div>
                  <div className="text-xs text-muted-foreground">
                    {[j.city, j.scheduled_date ? new Date(j.scheduled_date).toLocaleDateString() : ""].filter(Boolean).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
            <Button data-testid="measure-no-job" variant="outline" size="sm" className="w-full" onClick={skipJob}>
              {t("measure_no_job_manual")}
            </Button>
          </div>
        )}

        {/* -------- STEP 4: which PDF opening is unit N? -------- */}
        {step === "location" && (
          <div className="space-y-2">
            {unitProgress && (
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary" data-testid="measure-unit-progress">{unitProgress}</Badge>
                <Badge variant="outline" className="text-[10px]" data-testid="measure-unit-kind">
                  {units[uIdx]?.subtype
                    ? SUBTYPE_LABEL[units[uIdx].subtype]
                    : (units[uIdx]?.kind === "door" ? t("measure_door") : t("measure_window"))}
                </Badge>
              </div>
            )}
            {unitCrops[uIdx] && (
              <img
                src={unitCrops[uIdx]}
                alt=""
                className="w-full max-h-48 object-contain rounded-xl border bg-muted/40"
                data-testid="measure-unit-crop"
              />
            )}
            <p className="text-sm font-medium">{t("measure_pick_location")}</p>
            <div className="max-h-[36vh] overflow-y-auto space-y-1.5" data-testid="measure-openings-list">
              {(openings || []).map((o, i) => {
                const used = unitData.some((u, k) => k !== uIdx && u && u.label && u.label === o.label);
                return (
                  <button
                    key={`${o.location}-${i}`}
                    data-testid="measure-opening-item"
                    onClick={() => pickOpening(o)}
                    className={`w-full text-left rounded-xl border px-3 py-2 hover:bg-muted/60 transition-colors ${used ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{o.location}</span>
                      {o.label && <Badge variant="secondary" className="text-[9px]">{o.label}</Badge>}
                      {used && <Badge variant="outline" className="text-[9px]">{t("measure_used")}</Badge>}
                      <span className="ml-auto text-[10px] uppercase text-muted-foreground">
                        {SUBTYPE_LABEL[o.subtype] || (o.kind === "door" ? t("measure_door") : t("measure_window"))}
                        {o.shape && o.shape !== "rectangle" ? ` · ${t(`shape_${o.shape}`)}` : ""}
                      </span>
                    </div>
                    {o.size && <div className="font-mono-plex text-xs text-muted-foreground mt-0.5">{o.size}</div>}
                  </button>
                );
              })}
            </div>
            <Button data-testid="measure-other-location" variant="outline" size="sm" className="w-full" onClick={() => pickOpening(null)}>
              {t("measure_other_location")}
            </Button>
          </div>
        )}

        {/* -------- STEP 5: real measured values (per unit) -------- */}
        {step === "value" && (
          <div className="space-y-3 min-w-0 max-w-full overflow-x-hidden">
            {unitProgress && <Badge variant="secondary" data-testid="measure-unit-progress">{unitProgress}</Badge>}
            <div className="space-y-1">
              <Label className="text-xs">{t("measure_unit_type")}</Label>
              <WindowTypePicker
                key={uIdx}
                groups={[[t("measure_windows"), UNIT_SUBTYPES.window], [t("measure_doors"), UNIT_SUBTYPES.door]]}
                value={units[uIdx]?.subtype || ""}
                onChange={(v) => {
                  const door = !!v && DOOR_IDS.has(v);
                  setUnitProp(uIdx, { subtype: v, ...(v ? { kind: door ? "door" : "window" } : {}) });
                  // storm doors never carry sidelights/transom; windows never use door configs
                  if (door && (v === "storm" || !isDoorShape(shape))) { setShape("door"); setDims((d) => ({ w: d.w, h: d.h })); }
                  if (v && !door && isDoorShape(shape)) { setShape("rectangle"); setDims((d) => ({ w: d.w, h: d.h })); }
                }}
                noneLabel={t("measure_type_none")}
                t={t}
              />
            </div>
            {unitCrops[uIdx] && (
              <img
                src={unitCrops[uIdx]}
                alt=""
                className="w-full max-h-48 object-contain rounded-xl border bg-muted/40"
                data-testid="measure-unit-crop"
              />
            )}
            <div>
              <Label className="text-xs">{t("location")}</Label>
              <Input data-testid="measurement-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Master Bathroom" className="mt-1" />
            </div>
            {opening?.size && (
              <div className="rounded-lg bg-muted/60 px-3 py-2 text-sm flex items-center justify-between" data-testid="measure-expected">
                <span className="text-xs text-muted-foreground">{t("measure_expected")}</span>
                <span className="font-mono-plex font-semibold">{opening.size}</span>
              </div>
            )}
            {units[uIdx]?.kind === "door"
              ? <ShapeDimsEditor kind="door" shape={isDoorShape(shape) ? shape : "door"} dims={dims} hideShapes={units[uIdx]?.subtype === "storm"}
                  onShape={(sId) => { setShape(sId); setDims({ w: dims.w, h: dims.h, side: dims.side }); }} onDims={setDims} />
              : <ShapeDimsEditor shape={shape} dims={dims} onShape={(sId) => { setShape(sId); setDims({ side: dims.side }); }} onDims={setDims} />}
            {shapeDiverges && (
              <div data-testid="measure-shape-diverges" className="text-xs rounded-lg px-3 py-2 bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                ⚠ {t("measure_shape_diverges").replace("{pdf}", t(`shape_${pdfShape}`)).replace("{chosen}", t(`shape_${shape}`))}
              </div>
            )}
            {opening && realW.trim() && realH.trim() && (
              <div
                data-testid="measure-match-badge"
                className={`text-xs font-bold rounded-lg px-3 py-2 ${match ? "bg-emerald-500/10 text-emerald-600" : "bg-destructive/10 text-destructive"}`}
              >
                {match ? `✓ ${t("measure_match")}` : `✗ ${shapeDiverges && matchW && matchH ? t("measure_shape_wrong") : t("measure_mismatch")}`}
              </div>
            )}
            <div>
              <Label className="text-xs">{t("note")}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input data-testid="measurement-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="flex-1" />
                <VoiceInputButton onTranscript={(txt) => setNote((prev) => (prev ? `${prev} ${txt}` : txt))} className="shrink-0" />
              </div>
            </div>
            <Button data-testid="measure-to-preview" className="w-full" disabled={!unitReady} onClick={saveUnit}>
              {uIdx + 1 < units.length
                ? t("measure_next_piece")
                : units.length > 1 ? t("measure_continue") : t("measure_preview_btn")}
            </Button>
          </div>
        )}

        {/* -------- STEP 6: optional OVERALL opening measurement -------- */}
        {step === "overall" && (
          <div className="space-y-3">
            <p className="text-sm font-medium" data-testid="measure-overall-question">{t("measure_overall_q")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{t("measure_overall_w")}</Label>
                <Input data-testid="measure-overall-w" value={overallW} onChange={(e) => setOverallW(e.target.value)} placeholder='72"' className="mt-1 font-mono-plex" />
              </div>
              <div>
                <Label className="text-xs">{t("measure_overall_h")}</Label>
                <Input data-testid="measure-overall-h" value={overallH} onChange={(e) => setOverallH(e.target.value)} placeholder='80"' className="mt-1 font-mono-plex" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="measure-overall-skip" variant="outline" className="flex-1" onClick={() => finishOverall(false)}>
                {t("measure_skip")}
              </Button>
              <Button data-testid="measure-overall-add" className="flex-1" disabled={!overallW.trim() || !overallH.trim()} onClick={() => finishOverall(true)}>
                {t("measure_preview_btn")}
              </Button>
            </div>
          </div>
        )}

        {/* -------- STEP 7: annotated preview → add to the report -------- */}
        {step === "preview" && annotated && (
          <div className="space-y-3">
            <img src={annotated} alt="" className="w-full rounded-xl border" data-testid="measure-annotated-preview" />
            <p className="text-xs text-muted-foreground">{t("measure_preview_hint")}</p>
            {/* optional extra shots of this opening (other angle, rot, detail) — thumbnails next to the main photo */}
            <div className="rounded-xl border bg-muted/30 p-2.5 space-y-1.5" data-testid="measure-extra-photos">
              <div className="text-xs font-semibold">{t("measure_extra_photos")} <span className="font-normal text-muted-foreground">({t("optional")})</span></div>
              <div className="flex gap-1.5 flex-wrap">
                {extraPhotos.map((x, i) => (
                  <div key={x.mediaId || i} className="relative h-14 w-14 shrink-0" data-testid="measure-extra-thumb">
                    <img src={x.mediaId ? mediaSrcById(x.mediaId) : x.dataUrl} alt="" className="h-14 w-14 rounded-lg object-cover border" />
                    <button type="button" aria-label={t("remove")} data-testid="measure-extra-remove"
                            onClick={() => setExtraPhotos((prev) => prev.filter((_, k) => k !== i))}
                            className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center shadow">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {extraPhotos.length < 10 && (
                  <button type="button" data-testid="measure-extra-add" onClick={() => extraRef.current?.click()}
                          className="h-14 w-14 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/60">
                    <Plus className="h-5 w-5" />
                    <span className="text-[9px]">{t("add")}</span>
                  </button>
                )}
              </div>
              <input ref={extraRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickExtra} data-testid="measure-extra-input" />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setStep(units.length > 1 ? "overall" : "value")}
                data-testid="measure-back-btn"
              >
                {t("back")}
              </Button>
              {editing ? (
                <Button data-testid="measurement-save-edit" className="flex-1" onClick={saveEdit} disabled={saving}>
                  <Check className="h-4 w-4 mr-1" /> {saving ? t("sending") : t("measure_save_edit")}
                </Button>
              ) : (
                <Button data-testid="measurement-submit" className="flex-1" onClick={addToReport}>
                  <Plus className="h-4 w-4 mr-1" /> {t("measure_add_btn")}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* -------- STEP 8: any other wrong window? / send report -------- */}
        {step === "more" && (
          <div className="space-y-3 py-2">
            <div className="text-center">
              <p className="text-sm font-semibold text-emerald-600" data-testid="measure-added-flag">
                ✓ {t("measure_added")} ({items.length} {t("measure_in_report")})
              </p>
              <p className="text-sm font-medium mt-2" data-testid="measure-more-question">{t("measure_more_q")}</p>
            </div>
            {/* mini catalog preview */}
            {items.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="measure-report-strip">
                {items.map((it, i) => (
                  <img key={i} src={it.photo} alt="" className="h-14 w-14 rounded-lg object-cover border shrink-0" />
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button data-testid="measure-more-yes" variant="outline" className="flex-1" onClick={resetShot} disabled={saving}>
                <Camera className="h-4 w-4 mr-1" /> {t("measure_more_yes")}
              </Button>
              <Button data-testid="measure-send-all" className="flex-1" onClick={sendAll} disabled={saving}>
                {saving ? t("sending") : `${t("measure_send_all")} (${items.length})`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
